/* The counterparty card in a sales detail page's rail — ONE component for both `/sales/orders/:id`
   and `/sales/returns/:id`.

   Shared rather than copied because the backend already made that call: `picsell-api`'s
   `CounterpartyDetailSerializer` is nested by `OrderDetailSerializer` AND `ReturnDetailSerializer`,
   and its own docstring says it is "named for the position it fills (detail side panel), not for
   one owning resource". Two components against one deliberately shared serializer is the front end
   contradicting a decision already taken — and the drift is not hypothetical: returns shipped with
   `{id, name, address}` while orders showed five fields, for exactly as long as this was a copy.

   ⚠ THE LANG GROUP IS A PROP, NOT AN IMPORT. `docs/design/spec/04-content-i18n.md` and CLAUDE.md's
   rule — a page does not read another page's lang group even for identical copy — so each module
   keeps its own keys and hands the group in. That makes the key contract implicit, which is what
   `REQUIRED_KEYS` plus the dev warning below exist to make loud:

     k_counterparty  k_name  k_address  k_phone  k_remaining_funds

   plus whatever `actionKey` the caller passes for the button. `translate()` already warns once per
   missing key name in dev (`util/util.js`), but it cannot say WHICH group was short — with two
   callers that is the only part worth knowing, so the check here names the caller (via `actionKey`)
   and lists everything missing at once.

   `remaining_funds` arrives as a STRING from the API ("0.00", "-187559.23"); `formatMoney` runs its
   argument through `Number()` before `.toFixed(2)`, so no coercion is needed here — verified live,
   and the minus survives that round trip. A genuinely absent value renders `dash`, never an empty
   cell: an empty cell reads as an oversight, a dash reads as a fact.

   ⚠ `current_debt` was a sixth row until the API stopped sending it on BOTH detail endpoints
   (`excalibur/orders/{id}/` and `excalibur/returns/{id}/`). The row is REMOVED rather than left to
   render a permanent dash: a dash here would claim the debt exists for this counterparty and is
   merely missing on this record, which is the opposite of what the response now says.

   `remaining_funds` is not that number under a new name. It is a signed BALANCE — negative on live
   rows (-187559.23) — so reusing the debt label for it, or dropping the sign to make it "read
   better", inverts the one figure in this card a supervisor acts on. Neither is ours to flip. */

import React from "react";
import { useHistory } from "react-router-dom";
import { Button, Descriptions, Typography } from "antd";
import { LinkOutlined } from "@ant-design/icons";

import { dash, textOf } from "./table-cells";
import { formatMoney } from "../util/formatter";
import { translate } from "../util/util";

const REQUIRED_KEYS = ["k_counterparty", "k_name", "k_address", "k_phone", "k_remaining_funds"];

/* Same shape as `translate()`'s own guard: dev only, deduped, one line. Keyed by the message so a
   caller that is already correct never warns, and a caller that is short warns once per boot
   rather than once per render. */
const warned = new Set();

function warnMissingKeys(group, actionKey) {
  const missing = [...REQUIRED_KEYS, actionKey].filter(key => !group || !group[key]);
  if (!missing.length) return;
  const message = `CounterpartySection: the lang group behind "${actionKey}" is missing ${missing.join(", ")}`;
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(message);
}

/**
 * @param group     the caller's own lang group (ORDERS_PAGE / RETURNS_PAGE)
 * @param actionKey key in that group for the button's label
 * @param listPath  where the button goes; `?counterparty=<id>` is appended, and the param name is
 *                  the same on both lists, which is why one path prop covers the whole difference
 */
export default function CounterpartySection({ item, lang, group, actionKey, listPath }) {
  const history = useHistory();
  const t = key => translate(group, key, lang);
  const counterparty = item?.counterparty;

  if (process.env.NODE_ENV !== "production") warnMissingKeys(group, actionKey);

  if (!counterparty) return null;

  const money = value => (value === null || value === undefined || value === "" ? dash : <span className="lp-num">{formatMoney(value)}</span>);

  return (
    <div className="dp-card">
      <Typography.Text strong className="dp-sec-title">
        {t("k_counterparty")}
      </Typography.Text>
      <Descriptions column={1} size="small" colon={false}>
        {/* Not `k_counterparty` again — the card title already says it; a second "Контрагент:"
           label on the very next line would read as a mistake, not emphasis. */}
        <Descriptions.Item label={t("k_name")}>{textOf(counterparty.name)}</Descriptions.Item>
        <Descriptions.Item label={t("k_address")}>{textOf(counterparty.address)}</Descriptions.Item>
        {/* Every row is always present, a missing value shown as a dash — a card whose rows appear
            and disappear per record makes two records of the same kind impossible to compare, and
            was how the phone row used to behave here. */}
        <Descriptions.Item label={t("k_phone")}>{textOf(counterparty.phone_number)}</Descriptions.Item>
        {/* "Залишок коштів" — confirmed by product, and neither "переплата" nor "заборгованість",
           nor the debt row this replaced in position only. The balance is negative, positive or
           zero, all three legitimate, so it is shown signed and a zero shows as `0.00`: only a
           genuinely absent value dashes. See the header note. */}
        <Descriptions.Item label={t("k_remaining_funds")}>{money(counterparty.remaining_funds)}</Descriptions.Item>
      </Descriptions>
      {/* A plain internal navigation, not a leave-the-app one — `ExportOutlined` (spec/02) is
         reserved for that. `LinkOutlined` matches its own use two cards over, on "Візит": an
         in-app cross-reference to a related record's own filtered view. Plain `push`: this leaves
         the record for a different screen, unlike a filter change inside the list itself, which
         always `replace`s. `counterparty.id` is a UUID — safe unescaped in a query string. */}
      <Button icon={<LinkOutlined />} block onClick={() => history.push(`${listPath}?counterparty=${counterparty.id}`)}>
        {t(actionKey)}
      </Button>
    </div>
  );
}
